// utils/filterSessionData.js

/**
 * Normalize any two station values into a consistent { low, high } range.
 * Handles null/undefined values and ensures valid numeric bounds.
 * @param {number|null|undefined} rawStart - The start station, in feet.
 * @param {number|null|undefined} rawEnd   - The end station, in feet.
 * @param {{ defaultStart: number, defaultEnd: number }} fallback
 * @returns {{ low: number, high: number, isReversed: boolean, isEmpty: boolean }}
 */
export function normalizeBounds(
    rawStart,
    rawEnd,
    fallback = { defaultStart: 0, defaultEnd: 0 }
  ) {
    // 1. Both undefined → treat as empty
    if (rawStart == null && rawEnd == null) {
      return {
        low: fallback.defaultStart,
        high: fallback.defaultEnd,
        isReversed: false,
        isEmpty: true
      };
    }
  
    // 2. Substitute fallbacks where needed
    const start = rawStart != null ? Number(rawStart) : fallback.defaultStart;
    const end   = rawEnd   != null ? Number(rawEnd)   : fallback.defaultEnd;
  
    // 3. Validate
    if (isNaN(start) || isNaN(end)) {
      console.warn('Invalid station values provided:', { rawStart, rawEnd });
      return {
        low: fallback.defaultStart,
        high: fallback.defaultEnd,
        isReversed: false,
        isEmpty: true
      };
    }
  
    const isReversed = start > end;
  
    return {
      low: Math.min(start, end),
      high: Math.max(start, end),
      isReversed,
      isEmpty: false
    };
  }
  
  /** ---------- Segment helpers ---------- **/
  
  // True if any part of the segment lies inside [low, high]
  function segmentOverlapsBounds(segment, low, high) {
    const segStart = segment.from_station_ft  ?? segment.start_station_ft ?? 0;
    const segEnd   = segment.to_station_ft    ?? segment.end_station_ft   ?? segStart;
    return segStart <= high && segEnd >= low;
  }
  
  // Return a *clipped copy* of the segment
  function clipSegmentToBounds(segment, low, high) {
    const originalStart = segment.from_station_ft ?? segment.start_station_ft ?? 0;
    const originalEnd   = segment.to_station_ft   ?? segment.end_station_ft   ?? originalStart;
  
    return {
      ...segment,
      from_station_ft: Math.max(originalStart, low),
      to_station_ft:   Math.min(originalEnd,   high)
    };
  }
  
  /**
   * Filter & **clip** segments to the user-specified bounds.
   */
  export function filterSegmentsByBounds(
    segments = [],
    rawStart,
    rawEnd,
    sessionBounds = {}
  ) {
    if (!Array.isArray(segments) || segments.length === 0) return [];
  
    const bounds = normalizeBounds(rawStart, rawEnd, {
      defaultStart: sessionBounds.start_station_ft ?? 0,
      defaultEnd:   sessionBounds.end_station_ft   ?? 0
    });
  
    if (bounds.isEmpty && rawStart == null && rawEnd == null) return segments;
  
    return segments
      .filter(s => segmentOverlapsBounds(s, bounds.low, bounds.high))
      .map(s => clipSegmentToBounds(s, bounds.low, bounds.high));
  }
  
  /** ---------- Event helpers ---------- **/
  
  function eventWithinBounds(event, low, high) {
    const pos =
        event.from_station_ft ??
        event.station_ft      ??
        event.start_station_ft ??
        event.position_ft     ??
        0;
    return pos >= low && pos <= high;
  }
  
  /**
   * Filter events whose position lies inside the bounds.
   * (Events are points, so clipping is unnecessary.)
   */
  export function filterEventsByBounds(
    events = [],
    rawStart,
    rawEnd,
    sessionBounds = {}
  ) {
    if (!Array.isArray(events) || events.length === 0) return [];
  
    const bounds = normalizeBounds(rawStart, rawEnd, {
      defaultStart: sessionBounds.start_station_ft ?? 0,
      defaultEnd:   sessionBounds.end_station_ft   ?? 0
    });
  
    if (bounds.isEmpty && rawStart == null && rawEnd == null) return events;
  
    return events.filter(e => eventWithinBounds(e, bounds.low, bounds.high));
  }
  
  /**
   * Convenience wrapper to filter & clip both segments and events.
   */
  export function filterSessionData({
    segments,
    events,
    rawStart,
    rawEnd,
    sessionBounds = {}
  }) {
    const bounds = normalizeBounds(rawStart, rawEnd, {
      defaultStart: sessionBounds.start_station_ft ?? 0,
      defaultEnd:   sessionBounds.end_station_ft   ?? 0
    });
  
    const filteredSegments = filterSegmentsByBounds(
      segments,
      rawStart,
      rawEnd,
      sessionBounds
    );
    const filteredEvents = filterEventsByBounds(
      events,
      rawStart,
      rawEnd,
      sessionBounds
    );
  
    return {
      segments: filteredSegments,
      events: filteredEvents,
      bounds,
      stats: {
        originalSegments: segments?.length ?? 0,
        filteredSegments: filteredSegments.length,
        originalEvents: events?.length ?? 0,
        filteredEvents: filteredEvents.length
      }
    };
  }
  
  /** ---------- Station string utilities ---------- **/
  
  export function parseStationString(stationStr) {
    if (!stationStr || typeof stationStr !== 'string') {
      return { isValid: false, feet: null, error: 'Invalid input' };
    }
  
    const trimmed = stationStr.trim();
  
    // Pure numeric
    if (!trimmed.includes('+')) {
      const feet = parseFloat(trimmed);
      if (isNaN(feet)) {
        return { isValid: false, feet: null, error: 'Invalid numeric value' };
      }
      return { isValid: true, feet, error: null };
    }
  
    // XXX+YY.Y format
    const parts = trimmed.split('+');
    if (parts.length !== 2) {
      return {
        isValid: false,
        feet: null,
        error: 'Invalid station format. Use XXX+YY.Y'
      };
    }
  
    const [hundreds, remainder] = parts;
    const hundredsNum  = parseFloat(hundreds);
    const remainderNum = parseFloat(remainder);
  
    if (isNaN(hundredsNum) || isNaN(remainderNum)) {
      return { isValid: false, feet: null, error: 'Invalid numeric values' };
    }
    if (remainderNum >= 100) {
      return { isValid: false, feet: null, error: 'Remainder must be < 100' };
    }
  
    const feet = hundredsNum * 100 + remainderNum;
    return { isValid: true, feet, error: null };
  }
  
  export function formatStationFromFeet(feet) {
    if (feet == null || isNaN(feet)) return 'N/A';
    const hundreds  = Math.floor(feet / 100);
    const remainder = feet % 100;
    return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
  }
  